/**
 * 「参与请求」集合（2026-10-10 口径定稿）——纯逻辑模块，可 node 直跑断言。
 *
 * 口径：
 *   1. **参与请求**（决定向后端发送哪些对象）**只增不减**：任何"取消"动作
 *      —— 取消按钮 / 取消勾选「画布展示」确认框 / 取消全选 —— 都**不会**把对象
 *      从请求里移除。因此被隐藏的对象数据照常运行，撞到 ASC/DESC/MC/IC 四轴
 *      照常参与点/面报警判定（该触发报警就触发）；
 *   2. **画布展示**（visibleOnCanvas）是唯一受"取消"影响的状态；取消 = 不绘制
 *      （等价于"透明隐藏"），对象本身仍留在请求里；
 *   3. 恒星管控名单的合并（utils/starControl.js 的 withControlledStars）
 *      完全不受本模块影响，恒星管控业务逻辑保持原样。
 *
 * 设计动机：历史实现里"点按钮取消 = selected 置 false"，而 selected 同时喂给
 * 后端请求参数，于是取消按钮/取消勾选会把对象从请求里摘掉，导致该对象既不在
 * 画布展示、也不再参与报警判定。本模块把"是否发送"与"是否绘制"彻底分开。
 *
 * 纯逻辑（CommonJS），不依赖 Vue / window，便于 node 直跑断言。
 */

/** 记录一个参与请求的对象 id（只增不减、整体去重、剔除空值） */
function markParticipation (ids, id) {
  const merged = new Set()
  ;(Array.isArray(ids) ? ids : []).forEach(item => {
    if (item !== undefined && item !== null) {
      merged.add(item)
    }
  })
  if (id !== undefined && id !== null) {
    merged.add(id)
  }
  return Array.from(merged)
}

/** 把列表里"当前已选中"的对象并入参与集合（并集，不做删除） */
function seedParticipation (ids, list) {
  let next = Array.isArray(ids) ? ids.slice() : []
  ;(Array.isArray(list) ? list : []).forEach(item => {
    if (item && item.selected === true) {
      next = markParticipation(next, item.id)
    }
  })
  return next
}

/**
 * 提交给宿主的请求对象列表 = 参与集合 ∪ 当前已选中。
 * 关键点：**并集**，绝不因为某个对象当前未选中而把它剔除。
 */
function resolveRequestIds (ids, list) {
  const merged = new Set(Array.isArray(ids) ? ids : [])
  ;(Array.isArray(list) ? list : []).forEach(item => {
    if (item && item.selected === true && item.id !== undefined && item.id !== null) {
      merged.add(item.id)
    }
  })
  return Array.from(merged)
}

/**
 * 「取消全选」时保留的必选对象 id。
 *
 * 为什么需要它：参与集合"只增不减"能让单个对象的取消不丢数据，但如果**全选**也
 * 永久并进集合，请求参数就会被放大成全量且再也收不回来。因此约定：
 *   全选     → 全部并入（用户主动要全部）；
 *   取消全选 → 把集合收回只剩「必选对象」（恒星=受管控名单，其余调用方传 () => false），
 *              从而可以把历史上被全选放大的请求参数收干净。
 *
 * @param {Array} list 对象列表
 * @param {Function} isMandatory 判定"必选"（如 starControl.isControlledStar）
 */
function onlyMandatoryIds (list, isMandatory) {
  if (typeof isMandatory !== 'function') {
    return []
  }
  return (Array.isArray(list) ? list : [])
    .filter(item => item && isMandatory(item) === true)
    .map(item => item.id)
    .filter(id => id !== undefined && id !== null)
}

module.exports = {
  markParticipation,
  seedParticipation,
  resolveRequestIds,
  onlyMandatoryIds
}
